import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  AlertTitle,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  FormControlLabel,
  Switch,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assessment as AssessmentIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  FileCopy as CloneIcon,
  HelpOutline as HelpIcon,
  ViewList,
  AccountTree
} from '@mui/icons-material';
import api from '../../services/api';
import VisualFlowEditor from './onboarding-flow-visual-editor/VisualFlowEditor';

const OnboardingFlowsManager = () => {
  const [flows, setFlows] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [tools, setTools] = useState([]);
  const [assignmentRules, setAssignmentRules] = useState([]);
  const [triageStrategies, setTriageStrategies] = useState([]);
  const [treatmentPathways, setTreatmentPathways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [currentAnalytics, setCurrentAnalytics] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(() => {
    const saved = localStorage.getItem('onboardingFlows_includeInactive');
    return saved ? JSON.parse(saved) : false;
  });
  const [tabValue, setTabValue] = useState(0);
  const [stagesEditorMode, setStagesEditorMode] = useState('visual');

  // Clone dialog state
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloneSlug, setCloneSlug] = useState('');
  const [cloneSlugError, setCloneSlugError] = useState('');
  const [selectedFlow, setSelectedFlow] = useState(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState([]);
  const [referenceWarnings, setReferenceWarnings] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    isActive: true,
    stages: [{
      stageNumber: 1,
      stageName: 'Initial Onboarding',
      personaId: '',
      availableTools: [],
      requiredAssessments: [],
      transitionTrigger: { type: 'tool_success', toolName: 'updateUserProfile' },
      greetingMessage: '',
      transitionMessage: ''
    }],
    completionConfig: {
      therapistPersonaId: '',
      assignmentRuleId: null,
      completionMessage: '',
      therapistGreeting: '',
      dynamicAssignment: {
        enabled: false,
        strategy: 'triage_based',
        fallbackPersona: 'generalist'
      },
      triageStrategyId: null,
      triageMode: null,
      actions: []
    }
  });

  useEffect(() => {
    fetchData();
  }, [includeInactive]);

  useEffect(() => {
    localStorage.setItem('onboardingFlows_includeInactive', JSON.stringify(includeInactive));
  }, [includeInactive]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [flowsRes, personasRes, toolsRes, rulesRes, strategiesRes, platformRes] = await Promise.all([
        api.get(`/admin/onboarding-flows?includeInactive=${includeInactive}`),
        api.get('/personas'), // Correct endpoint
        api.get('/tools'), // Correct endpoint
        api.get('/admin/assignment-rules'),
        api.get('/admin/triage-strategies?includeInactive=false'),
        api.get('/platform-settings/settings')
      ]);

      setFlows(flowsRes.data.flows || []);
      setPersonas(personasRes.data || []); // Personas endpoint returns array directly
      setTools(toolsRes.data || []); // Tools endpoint returns array directly
      setAssignmentRules(rulesRes.data.rules || []);
      setTriageStrategies(strategiesRes.data.strategies || []);
      setTreatmentPathways(platformRes.data?.treatmentPathways || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (flow = null) => {
    // Clear validation state
    setValidationErrors([]);
    setReferenceWarnings([]);

    if (flow) {
      setCurrentFlow(flow);
      const processedFormData = {
        name: flow.name,
        slug: flow.slug,
        description: flow.description || '',
        isActive: flow.isActive,
        stages: flow.stages.map(stage => ({
          ...stage,
          personaId: stage.personaId?._id || stage.personaId,
          availableTools: stage.availableTools.map(t => ({
            toolId: t.toolId?._id || t.toolId,
            enabled: t.enabled,
            customConfig: t.customConfig || {}
          }))
        })),
        completionConfig: {
          therapistPersonaId: flow.completionConfig.therapistPersonaId?._id || flow.completionConfig.therapistPersonaId,
          assignmentRuleId: flow.completionConfig.assignmentRuleId?._id || flow.completionConfig.assignmentRuleId || null,
          completionMessage: flow.completionConfig.completionMessage || '',
          therapistGreeting: flow.completionConfig.therapistGreeting || '',
          dynamicAssignment: flow.completionConfig.dynamicAssignment || {
            enabled: false,
            strategy: 'triage_based',
            fallbackPersona: 'generalist'
          },
          triageStrategyId: flow.completionConfig.triageStrategyId || null,
          triageMode: flow.completionConfig.triageMode || null,
          actions: flow.completionConfig.actions || []
        }
      };
      setFormData(processedFormData);

      // Check for broken references
      const warnings = validateFlowReferences(flow);
      setReferenceWarnings(warnings);
    } else {
      setCurrentFlow(null);
      // Get default persona (generalist or first available)
      const defaultPersona = personas.find(p => p.key === 'generalist') || personas[0];
      const defaultPersonaId = defaultPersona?._id || '';

      setFormData({
        name: '',
        slug: '',
        description: '',
        isActive: false, // New flows start inactive for safety
        stages: [{
          stageNumber: 1,
          stageName: 'Initial Onboarding',
          personaId: defaultPersonaId,
          availableTools: [],
          requiredAssessments: [],
          transitionTrigger: { type: 'tool_success', toolName: '', delayMinutes: 0 }, // Default to tool_success
          greetingMessage: '',
          transitionMessage: ''
        }],
        completionConfig: {
          therapistPersonaId: defaultPersonaId,
          assignmentRuleId: null,
          completionMessage: '',
          therapistGreeting: '',
          dynamicAssignment: {
            enabled: false,
            strategy: 'triage_based',
            fallbackPersona: 'generalist'
          },
          triageStrategyId: null,
          triageMode: null,
          actions: []
        }
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentFlow(null);
  };

  const handleSubmit = async () => {
    // Validate before saving
    if (!validateBeforeSave()) {
      setError('Please fix the validation errors before saving');
      return;
    }

    try {
      // Generate slug from name if not provided
      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

      const payload = {
        ...formData,
        slug
      };

      if (currentFlow) {
        await api.put(`/admin/onboarding-flows/${currentFlow._id}`, payload);
        setSuccess('Onboarding flow updated successfully');
      } else {
        await api.post('/admin/onboarding-flows', payload);
        setSuccess('Onboarding flow created successfully');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      console.error('Error saving flow:', err);
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Error saving flow');
    }
  };

  const handleToggleActive = async (flowId) => {
    try {
      await api.patch(`/admin/onboarding-flows/${flowId}/toggle-active`);
      setSuccess('Flow status updated successfully');
      fetchData();
    } catch (err) {
      console.error('Error toggling flow:', err);
      setError(err.response?.data?.message || 'Error toggling flow status');
    }
  };

  const handleDelete = async (flowId) => {
    if (!window.confirm('Are you sure you want to deactivate this flow?')) {
      return;
    }

    try {
      await api.delete(`/admin/onboarding-flows/${flowId}`);
      setSuccess('Flow deactivated successfully');
      fetchData();
    } catch (err) {
      console.error('Error deleting flow:', err);
      setError(err.response?.data?.message || 'Error deleting flow');
    }
  };

  const handleViewAnalytics = async (flowId) => {
    try {
      const response = await api.get(`/admin/onboarding-flows/${flowId}/analytics`);
      setCurrentAnalytics(response.data.analytics);
      setAnalyticsDialogOpen(true);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.message || 'Error fetching analytics');
    }
  };

  const handleClone = (flow) => {
    setSelectedFlow(flow);
    setCloneName(`${flow.name} (Copy)`);
    setCloneSlug(`${flow.slug}-copy-${Date.now()}`);
    setCloneDialogOpen(true);
  };

  const handleCloneSubmit = async () => {
    try {
      if (!cloneName || !cloneSlug) {
        setError('Name and slug are required');
        return;
      }

      await api.post(`/admin/onboarding-flows/${selectedFlow._id}/clone`, {
        newName: cloneName,
        newSlug: cloneSlug
      });

      setSuccess('Onboarding flow cloned successfully (inactive by default - showing inactive flows)');
      setCloneDialogOpen(false);
      setSelectedFlow(null);
      setCloneName('');
      setCloneSlug('');

      // Automatically show inactive flows so user can see the clone
      setIncludeInactive(true);
    } catch (err) {
      console.error('Error cloning flow:', err);
      setError(err.response?.data?.message || 'Error cloning flow');
    }
  };

  const handleAddStage = () => {
    setFormData({
      ...formData,
      stages: [
        ...formData.stages,
        {
          stageNumber: formData.stages.length + 1,
          stageName: `Stage ${formData.stages.length + 1}`,
          personaId: '',
          availableTools: [],
          requiredAssessments: [],
          transitionTrigger: { type: 'tool_success', toolName: 'updateUserProfile' },
          greetingMessage: '',
          transitionMessage: ''
        }
      ]
    });
  };

  const handleRemoveStage = (index) => {
    const newStages = formData.stages.filter((_, i) => i !== index);
    // Renumber stages
    newStages.forEach((stage, i) => {
      stage.stageNumber = i + 1;
    });
    setFormData({ ...formData, stages: newStages });
  };

  const handleStageChange = (index, field, value) => {
    const newStages = [...formData.stages];
    newStages[index] = { ...newStages[index], [field]: value };
    setFormData({ ...formData, stages: newStages });
  };

  const handleAddToolToStage = (stageIndex) => {
    const newStages = [...formData.stages];
    newStages[stageIndex].availableTools.push({
      toolId: '',
      enabled: true,
      customConfig: {}
    });
    setFormData({ ...formData, stages: newStages });
  };

  const handleRemoveToolFromStage = (stageIndex, toolIndex) => {
    const newStages = [...formData.stages];
    newStages[stageIndex].availableTools.splice(toolIndex, 1);
    setFormData({ ...formData, stages: newStages });
  };

  const handleToolChange = (stageIndex, toolIndex, field, value) => {
    const newStages = [...formData.stages];
    newStages[stageIndex].availableTools[toolIndex][field] = value;
    setFormData({ ...formData, stages: newStages });
  };

  // Validation functions
  const validateSlug = (slug) => {
    if (!slug) return 'Slug is required';
    if (!/^[a-z0-9-]+$/.test(slug)) {
      return 'Slug can only contain lowercase letters, numbers, and hyphens';
    }
    if (slug.startsWith('-') || slug.endsWith('-')) {
      return 'Slug cannot start or end with a hyphen';
    }
    return '';
  };

  const validateCompletionConfig = (config) => {
    const hasPersona = config?.therapistPersonaId && config.therapistPersonaId.trim() !== '';
    const hasDynamic = config?.dynamicAssignment?.enabled === true;

    if (!hasPersona && !hasDynamic) {
      return 'Either select a completion therapist persona OR enable dynamic assignment';
    }

    if (hasDynamic) {
      if (!config.dynamicAssignment?.strategy) {
        return 'Dynamic assignment requires a strategy';
      }
      if (!config.dynamicAssignment?.fallbackPersona) {
        return 'Dynamic assignment requires a fallback persona';
      }
    }

    return '';
  };

  const validateBeforeSave = () => {
    const errors = [];

    // Validate name
    if (!formData.name || formData.name.trim() === '') {
      errors.push('Flow name is required');
    }

    // Validate stages have personas
    formData.stages.forEach((stage, index) => {
      if (!stage.personaId) {
        errors.push(`Stage ${index + 1} (${stage.stageName}): Persona is required`);
      }

      // Validate transition fields
      if (stage.transitionTrigger?.type === 'tool_success' && !stage.transitionTrigger?.toolName) {
        errors.push(`Stage ${index + 1} (${stage.stageName}): Tool name is required for tool_success transitions`);
      }
      if (stage.transitionTrigger?.type === 'time_based' && (!stage.transitionTrigger?.delayMinutes || stage.transitionTrigger.delayMinutes <= 0)) {
        errors.push(`Stage ${index + 1} (${stage.stageName}): Delay must be greater than 0 for time_based transitions`);
      }
    });

    // Validate completion config
    const completionError = validateCompletionConfig(formData.completionConfig);
    if (completionError) {
      errors.push(completionError);
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const validateFlowReferences = (flow) => {
    const warnings = [];

    // Check stage personas
    flow.stages?.forEach((stage, index) => {
      if (stage.personaId) {
        const personaExists = personas.find(p => p._id === stage.personaId || p._id === stage.personaId?._id);
        if (!personaExists) {
          warnings.push(`Stage ${index + 1}: Referenced persona no longer exists`);
        }
      }

      // Check stage tools
      stage.availableTools?.forEach((tool, toolIndex) => {
        const toolId = tool.toolId?._id || tool.toolId;
        if (toolId && !tools.find(t => t._id === toolId)) {
          warnings.push(`Stage ${index + 1}, Tool ${toolIndex + 1}: Referenced tool no longer exists`);
        }
      });
    });

    // Check completion persona
    const completionPersonaId = flow.completionConfig?.therapistPersonaId?._id || flow.completionConfig?.therapistPersonaId;
    if (completionPersonaId && !personas.find(p => p._id === completionPersonaId)) {
      warnings.push('Completion therapist persona no longer exists');
    }

    return warnings;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Onboarding Flows Management</Typography>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label="Show Inactive"
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create New Flow
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Stages</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Usage Stats</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flows.map((flow) => (
              <TableRow key={flow._id}>
                <TableCell>{flow.name}</TableCell>
                <TableCell>
                  <code>{flow.slug}</code>
                </TableCell>
                <TableCell>{flow.stages?.length || 0} stages</TableCell>
                <TableCell>
                  <Chip
                    label={flow.isActive ? 'Active' : 'Inactive'}
                    color={flow.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {flow.stats?.totalStarted || 0} started / {flow.stats?.totalCompleted || 0} completed
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(flow)}
                    title="Edit"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color={flow.isActive ? 'warning' : 'success'}
                    onClick={() => handleToggleActive(flow._id)}
                    title={flow.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {flow.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                  </IconButton>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => handleViewAnalytics(flow._id)}
                    title="View Analytics"
                  >
                    <AssessmentIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => handleClone(flow)}
                    title="Clone Flow"
                  >
                    <CloneIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(flow._id)}
                    title="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentFlow ? 'Edit Onboarding Flow' : 'Create New Onboarding Flow'}
        </DialogTitle>
        <DialogContent>
          {/* Reference Warnings */}
          {referenceWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Broken References Detected</AlertTitle>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {referenceWarnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Please update these references before saving.
              </Typography>
            </Alert>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Validation Errors</AlertTitle>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationErrors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="Basic Info" />
            <Tab label="Stages" />
            <Tab label="Completion" />
          </Tabs>

          {tabValue === 0 && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Flow Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Slug (URL-friendly name)"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                sx={{ mb: 2 }}
                helperText="Leave empty to auto-generate from name"
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
          )}

          {tabValue === 1 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <ToggleButtonGroup
                  value={stagesEditorMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setStagesEditorMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="visual">
                    <AccountTree sx={{ mr: 0.5 }} fontSize="small" />
                    Visual
                  </ToggleButton>
                  <ToggleButton value="form">
                    <ViewList sx={{ mr: 0.5 }} fontSize="small" />
                    Form
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {stagesEditorMode === 'form' && (
                <>
                  {formData.stages.map((stage, stageIndex) => (
                <Accordion key={stageIndex} defaultExpanded={stageIndex === 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Stage {stage.stageNumber}: {stage.stageName}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Stage Name"
                          value={stage.stageName}
                          onChange={(e) => handleStageChange(stageIndex, 'stageName', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            fullWidth
                            select
                            label="Persona"
                            value={stage.personaId}
                            onChange={(e) => handleStageChange(stageIndex, 'personaId', e.target.value)}
                            helperText="Controls how the AI assistant behaves during this stage"
                          >
                            {personas.map((persona) => (
                              <MenuItem key={persona._id} value={persona._id}>
                                {persona.name} ({persona.key})
                              </MenuItem>
                            ))}
                          </TextField>
                          <Tooltip
                            title="The persona determines the AI's tone, expertise, and conversation style. You can edit personas in the Admin Dashboard under 'Therapist Personas'."
                            arrow
                          >
                            <IconButton size="small" color="info">
                              <HelpIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Available Tools:</Typography>
                        {stage.availableTools.map((tool, toolIndex) => (
                          <Box key={toolIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              select
                              label="Tool"
                              value={tool.toolId}
                              onChange={(e) => handleToolChange(stageIndex, toolIndex, 'toolId', e.target.value)}
                              sx={{ flex: 1 }}
                            >
                              {tools.map((t) => (
                                <MenuItem key={t._id} value={t._id}>
                                  {t.name}
                                </MenuItem>
                              ))}
                            </TextField>
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveToolFromStage(stageIndex, toolIndex)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          size="small"
                          onClick={() => handleAddToolToStage(stageIndex)}
                          startIcon={<AddIcon />}
                        >
                          Add Tool
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Greeting Message"
                          value={stage.greetingMessage}
                          onChange={(e) => handleStageChange(stageIndex, 'greetingMessage', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Transition Message"
                          value={stage.transitionMessage}
                          onChange={(e) => handleStageChange(stageIndex, 'transitionMessage', e.target.value)}
                        />
                      </Grid>
                      {stageIndex > 0 && (
                        <Grid item xs={12}>
                          <Button
                            color="error"
                            onClick={() => handleRemoveStage(stageIndex)}
                            startIcon={<DeleteIcon />}
                          >
                            Remove Stage
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
              <Button
                sx={{ mt: 2 }}
                onClick={handleAddStage}
                startIcon={<AddIcon />}
                variant="outlined"
              >
                Add Stage
              </Button>
                </>
              )}

              {stagesEditorMode === 'visual' && (
                <VisualFlowEditor
                  flow={formData}
                  onFlowChange={setFormData}
                />
              )}
            </Box>
          )}

          {tabValue === 2 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                  fullWidth
                  select
                  label="Therapist Persona (post-onboarding)"
                  value={formData.completionConfig.therapistPersonaId}
                  onChange={(e) => setFormData({
                    ...formData,
                    completionConfig: { ...formData.completionConfig, therapistPersonaId: e.target.value }
                  })}
                  sx={{ mb: 2 }}
                  required
                  helperText="Default persona if triage is disabled - determines ongoing therapy behavior"
                >
                  {personas.map((persona) => (
                    <MenuItem key={persona._id} value={persona._id}>
                      {persona.name} ({persona.key})
                    </MenuItem>
                  ))}
                </TextField>
                <Tooltip
                  title="This sets the default AI therapist persona after onboarding completes. Enable Dynamic Persona Assignment below to use triage-based routing instead."
                  arrow
                >
                  <IconButton size="small" color="info" sx={{ mt: 1 }}>
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                select
                label="Assignment Rule (optional)"
                value={formData.completionConfig.assignmentRuleId || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  completionConfig: { ...formData.completionConfig, assignmentRuleId: e.target.value || null }
                })}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">None (use legacy auto-assignment)</MenuItem>
                {assignmentRules.map((rule) => (
                  <MenuItem key={rule._id} value={rule._id}>
                    {rule.name} ({rule.strategy?.type})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Completion Message"
                value={formData.completionConfig.completionMessage}
                onChange={(e) => setFormData({
                  ...formData,
                  completionConfig: { ...formData.completionConfig, completionMessage: e.target.value }
                })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Therapist Greeting"
                value={formData.completionConfig.therapistGreeting}
                onChange={(e) => setFormData({
                  ...formData,
                  completionConfig: { ...formData.completionConfig, therapistGreeting: e.target.value }
                })}
                sx={{ mb: 2 }}
              />

              {/* Triage Configuration */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.completionConfig.dynamicAssignment?.enabled || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        completionConfig: {
                          ...formData.completionConfig,
                          dynamicAssignment: {
                            ...formData.completionConfig.dynamicAssignment,
                            enabled: e.target.checked
                          }
                        }
                      })}
                    />
                  }
                  label="Enable Dynamic Persona Assignment (Triage)"
                />
                <Tooltip
                  title="When enabled, the system automatically assigns specialized therapist personas based on the client's assessment results and needs. Configure triage strategies below to customize routing rules."
                  arrow
                >
                  <IconButton size="small" color="info">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {formData.completionConfig.dynamicAssignment?.enabled && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="subtitle2">
                      Triage Configuration
                    </Typography>
                    <Tooltip
                      title="Triage strategies contain rules that analyze assessment results to determine which specialized therapist persona best matches the client's needs. Manage strategies in the Triage Strategies admin section."
                      arrow
                    >
                      <IconButton size="small" color="info">
                        <HelpIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <TextField
                    fullWidth
                    select
                    label="Triage Strategy"
                    value={formData.completionConfig.triageStrategyId || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      completionConfig: { ...formData.completionConfig, triageStrategyId: e.target.value || null }
                    })}
                    sx={{ mb: 2 }}
                    helperText="Choose a strategy or use default - strategies define rules for matching clients to specialized personas"
                  >
                    <MenuItem value="">Use Default Strategy</MenuItem>
                    {triageStrategies.map((strategy) => (
                      <MenuItem key={strategy._id} value={strategy._id}>
                        {strategy.name} ({strategy.defaultMode})
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    select
                    label="Triage Mode"
                    value={formData.completionConfig.triageMode || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      completionConfig: { ...formData.completionConfig, triageMode: e.target.value || null }
                    })}
                    sx={{ mb: 2 }}
                    helperText="Override strategy default mode (leave empty to use strategy default)"
                  >
                    <MenuItem value="">Use Strategy Default</MenuItem>
                    <MenuItem value="automatic">Automatic (Immediate Assignment)</MenuItem>
                    <MenuItem value="manual_review">Manual Review (Supervisor Approval)</MenuItem>
                  </TextField>

                  <TextField
                    fullWidth
                    select
                    label="Fallback Persona"
                    value={formData.completionConfig.dynamicAssignment?.fallbackPersona || 'generalist'}
                    onChange={(e) => setFormData({
                      ...formData,
                      completionConfig: {
                        ...formData.completionConfig,
                        dynamicAssignment: {
                          ...formData.completionConfig.dynamicAssignment,
                          fallbackPersona: e.target.value
                        }
                      }
                    })}
                    helperText="Used if triage routing fails"
                  >
                    {personas.map((persona) => (
                      <MenuItem key={persona._id} value={persona.key}>
                        {persona.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}

              {/* Treatment Pathway Assignment */}
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle2">
                    Completion Actions
                  </Typography>
                  <Tooltip
                    title="Actions that run when onboarding completes. Assign a treatment pathway to set the client's therapy program."
                    arrow
                  >
                    <IconButton size="small" color="info">
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  select
                  label="Assign Treatment Pathway"
                  value={formData.completionConfig.actions?.find(a => a.type === 'assign_pathway')?.config?.pathwayKey || ''}
                  onChange={(e) => {
                    const newActions = e.target.value
                      ? [{ type: 'assign_pathway', config: { pathwayKey: e.target.value } }]
                      : [];
                    setFormData({
                      ...formData,
                      completionConfig: { ...formData.completionConfig, actions: newActions }
                    });
                  }}
                  helperText="Treatment pathway assigned when onboarding completes"
                >
                  <MenuItem value="">None</MenuItem>
                  {treatmentPathways.map((pathway) => (
                    <MenuItem key={pathway.key || pathway._id} value={pathway.key}>
                      {pathway.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {currentFlow ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onClose={() => setAnalyticsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Flow Analytics</DialogTitle>
        <DialogContent>
          {currentAnalytics && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">{currentAnalytics.flowName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary">Total Started</Typography>
                    <Typography variant="h4">{currentAnalytics.stats.totalStarted}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary">Total Completed</Typography>
                    <Typography variant="h4">{currentAnalytics.stats.totalCompleted}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary">Completion Rate</Typography>
                    <Typography variant="h4">{currentAnalytics.metrics.completionRate}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary">Avg Duration</Typography>
                    <Typography variant="h4">{currentAnalytics.metrics.averageDuration}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary">In Progress</Typography>
                    <Typography variant="h4">{currentAnalytics.usage.inProgress}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary">Total Users</Typography>
                    <Typography variant="h4">{currentAnalytics.usage.totalUsers}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clone Onboarding Flow</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Create a copy of "{selectedFlow?.name}". The cloned flow will be inactive until you activate it.
            </Typography>
            <TextField
              fullWidth
              label="New Flow Name"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="New Slug (URL-friendly)"
              value={cloneSlug}
              onChange={(e) => {
                const newSlug = e.target.value.toLowerCase().replace(/\s+/g, '-');
                setCloneSlug(newSlug);
                setCloneSlugError(validateSlug(newSlug));
              }}
              required
              error={!!cloneSlugError}
              helperText={cloneSlugError || 'URL-friendly identifier (lowercase letters, numbers, hyphens only)'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCloneDialogOpen(false);
            setCloneSlugError('');
          }}>Cancel</Button>
          <Button
            onClick={handleCloneSubmit}
            variant="contained"
            color="primary"
            disabled={!cloneName || !cloneSlug || !!cloneSlugError}
          >
            Clone Flow
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OnboardingFlowsManager;
